import { Component, OnInit } from '@angular/core';
import { ApiServiceBase } from 'src/app/Services/api.service.base';
import { PermissionsService } from 'src/app/Services/permissions.service';

/**
 * Management page for the organization's transaction breakdown fee definitions: the fee
 * types (stage / value kind / basis calculation rules), the named fees shown as breakdown
 * lines, and the per-entity-type pricing modes controlling whether quoted amounts are
 * treated as inclusive (reverse-calculated, the default) or exclusive (fees added on top).
 */
@Component({
    selector: 'app-transactionbreakdown',
    templateUrl: './transactionbreakdown.component.html',
    standalone: false
})
export class TransactionbreakdownComponent implements OnInit {

  public FeeTypes: any[] | null = null;
  public Fees: any[] | null = null;
  public PricingModes: any[] | null = null;
  public TargetTypes: any[] | null = null;
  public CanWrite: boolean = false;
  public PreviewAmount: number | null = null;
  public PreviewTargetType: string = "";
  public PreviewPricingMode: number = 0;
  public Preview: any | null = null;
  public PreviewRequested: boolean = false;

  constructor(private apiService: ApiServiceBase, private permissionsService: PermissionsService) { }

  public ngOnInit(): void {
    this.Refresh();
    this.permissionsService.CheckAdminPermission('TransactionBreakdownFeature', 300).then(x => this.CanWrite = x);
    this.apiService.Get<any[]>("common/modeltypes/PaymentEntity").then(result => this.TargetTypes = result);
  }

  public Refresh(): void {
    this.apiService.Get<any[]>("payments/transactionbreakdown/feetypes").then(result => this.FeeTypes = result);
    this.apiService.Get<any[]>("payments/transactionbreakdown/fees").then(result => this.Fees = result);
    this.apiService.Get<any[]>("payments/transactionbreakdown/pricingmodes").then(result => this.PricingModes = result);
  }

  /**
   * Previews how a sample amount breaks down under the current active fee configuration.
   * With a target type selected, previews under that type's actual configured pricing mode.
   * Otherwise, the Pricing Mode selector forces inclusive/exclusive math directly - the way
   * untyped callers like the mobile app always calculate, with no target type involved.
   */
  public RunPreview(): void {
    if (this.PreviewAmount == null || this.PreviewAmount <= 0) return;
    this.PreviewRequested = true;
    let url = "payments/transactionbreakdown/preview?totalAmount=" + this.PreviewAmount;
    if (this.PreviewTargetType) {
      url += "&targetType=" + this.PreviewTargetType;
    } else {
      url += "&pricingMode=" + this.PreviewPricingMode;
    }
    this.apiService.Get<any>(url).then(result => this.Preview = result);
  }

  public PricingModeLabel(pricingMode: number): string {
    return pricingMode == 1 ? "Exclusive" : "Inclusive";
  }

  public StageLabel(stage: number): string {
    switch (stage) {
      case 100: return "Pre-tax Fee";
      case 200: return "Tax";
      case 300: return "Post-tax Fee";
      default: return "" + stage;
    }
  }

  public ValueKindLabel(valueKind: number): string {
    return valueKind == 1 ? "Flat Amount" : "Percentage";
  }

  public BasisLabel(basis: number): string {
    return basis == 1 ? "Stage Running Total" : "Base Parking Amount";
  }

  public TargetTypeLabel(targetType: string): string {
    return this.TargetTypes?.find(t => t.Name == targetType)?.DisplayName ?? targetType;
  }

  /**
   * Formats a fee's value for the list - "15%" for percentage fees, "0.50" for flat fees.
   */
  public FeeValueLabel(fee: any): string {
    return fee.FeeType?.ValueKind == 1 ? "" + fee.Value : fee.Value + "%";
  }
}
